import React from 'react';
import { Box, LinearProgress, Collapse } from '@mui/material';
import Text from '../atoms/Text';
import StatusIcon, { StatusType } from '../atoms/StatusIcon';

export interface TaskData {
  id: string;
  title: string;
  description?: string;
  status: StatusType;
  progress?: number;
  result?: string;
  error?: string;
  startTime?: Date;
  endTime?: Date;
}

export interface TaskStatusProps {
  task: TaskData;
  showDetails?: boolean;
  sx?: any;
}

const TaskStatus = ({
  task,
  showDetails = false,
  sx,
}: TaskStatusProps) => {
  const getDuration = () => {
    if (!task.startTime) return null;
    const end = task.endTime || new Date();
    const duration = end.getTime() - task.startTime.getTime();
    return duration > 1000 ? `${Math.round(duration / 1000)}s` : `${duration}ms`;
  };

  const getStatusText = () => {
    switch (task.status) {
      case 'pending': return 'Waiting to start...';
      case 'running': return 'In progress...';
      case 'completed': return 'Completed successfully';
      case 'error': return task.error || 'Failed to complete';
      case 'warning': return 'Completed with warnings';
      case 'paused': return 'Paused';
      default: return '';
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 1,
        },
        ...sx,
      }}
    >
      {/* Task Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          mb: showDetails ? 1 : 0,
        }}
      >
        <StatusIcon 
          status={task.status} 
          showProgress={task.status === 'running'}
          sx={{ mt: 0.5 }}
        />
        
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Text
            content={task.title}
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 0.5 }}
          />
          
          {task.description && (
            <Text
              content={task.description}
              variant="body2"
              color="textSecondary"
              maxLines={2}
            />
          )}
          
          <Text
            content={getStatusText()}
            variant="caption"
            color={task.status === 'error' ? 'error' : 'textSecondary'}
            sx={{ mt: 0.5 }}
          />
        </Box>

        {/* Duration */}
        {getDuration() && (
          <Text
            content={getDuration() || ''}
            variant="caption"
            color="textSecondary"
            sx={{ 
              bgcolor: 'grey.100', 
              px: 1, 
              py: 0.5, 
              borderRadius: 1,
              flexShrink: 0,
            }}
          />
        )}
      </Box>

      {/* Progress Bar */}
      {task.status === 'running' && typeof task.progress === 'number' && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={task.progress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Text content="" variant="caption" />
            <Text
              content={`${Math.round(task.progress)}%`}
              variant="caption"
              color="textSecondary"
            />
          </Box>
        </Box>
      )}

      {/* Detailed Information */}
      <Collapse in={showDetails}>
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          {task.result && (
            <Box sx={{ mb: 1 }}>
              <Text
                content="Result:"
                variant="caption"
                sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
              />
              <Text
                content={task.result}
                variant="body2"
                sx={{
                  bgcolor: 'success.light',
                  color: 'success.dark',
                  p: 1,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                }}
              />
            </Box>
          )}

          {task.error && (
            <Box sx={{ mb: 1 }}>
              <Text
                content="Error:"
                variant="caption"
                sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
              />
              <Text
                content={task.error}
                variant="body2"
                sx={{
                  bgcolor: 'error.light',
                  color: 'error.dark',
                  p: 1,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                }}
              />
            </Box>
          )}

          {/* Timestamps */}
          {(task.startTime || task.endTime) && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {task.startTime && (
                <Text
                  content={`Started: ${task.startTime.toLocaleTimeString()}`}
                  variant="caption"
                  color="textSecondary"
                />
              )}
              {task.endTime && (
                <Text
                  content={`Ended: ${task.endTime.toLocaleTimeString()}`}
                  variant="caption"
                  color="textSecondary"
                />
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default TaskStatus; 